import { useContext, useState, useMemo } from 'react'
import { Context } from '@entities/Context'
import { useNavigate } from 'react-router-dom'
import { isEmpty, trim, values, flattenDeep } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faCircleNotch, faReply } from '@fortawesome/free-solid-svg-icons'

// Moved regex patterns outside component to avoid recreation
const emailRegex = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i
const pinRegex = /^[0-9]{4,8}$/i

export default function Register() {
    const { api, storage } = useContext(Context)
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        emailConfirmation: '',
        pin: '',
        pinConfirmation: '',
        terms: false
    })

    // Computed values as memoized values
    const invalidEmail = useMemo(() =>
        !isEmpty(formData.email) && !emailRegex.test(formData.email),
        [formData.email]
    )

    const invalidEmailConfirmation = useMemo(() =>
        !isEmpty(formData.emailConfirmation) &&
        (!formData.email || formData.email !== formData.emailConfirmation),
        [formData.email, formData.emailConfirmation]
    )

    const invalidPin = useMemo(() =>
        !isEmpty(formData.pin) && !pinRegex.test(formData.pin),
        [formData.pin]
    )

    const invalidPinConfirmation = useMemo(() =>
        !isEmpty(formData.pinConfirmation) &&
        formData.pin !== formData.pinConfirmation,
        [formData.pin, formData.pinConfirmation]
    )

    const disabled = useMemo(() =>
        loading ||
        !formData.terms ||
        isEmpty(formData.name) ||
        isEmpty(formData.email) ||
        isEmpty(formData.pin) ||
        invalidEmail ||
        invalidPin ||
        formData.email !== formData.emailConfirmation ||
        formData.pin !== formData.pinConfirmation,
        [
            loading,
            formData.terms,
            formData.name,
            formData.email,
            formData.pin,
            formData.emailConfirmation,
            formData.pinConfirmation,
            invalidEmail,
            invalidPin
        ]
    )

    // Event handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        let newValue = value

        // PIN validation (numbers only)
        if (name === 'pin' || name === 'pinConfirmation') {
            newValue = value.replace(/[^0-9]+/, '')
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : newValue
        }))
    }

    const handleBack = () => {
        navigate('/welcome', { replace: true })
    }

    const handleRegister = async () => {
        if (loading) return
        setLoading(true)
        setError(false)

        try {
            const { data } = await api.post('/api/account', {
                name: formData.name,
                email: formData.email,
                pin: formData.pin,
            })

            storage.set('pin', formData.pin)
            storage.set('account', {
                id: data.data.id,
                name: data.data.name,
                email: data.data.email,
                permissions: data.data.permissions,
                metadata: data.data.metadata,
            })

            navigate('/dashboard', { replace: true })
        } catch (error) {
            setError(trim(
                error.response.data.message +
                ' ' + flattenDeep(values(error.response.data.errors || {})).join(' ')
            ))
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <p className="mb-6 text-lg font-light">
                Let's get your account setup. Payments are securely processed through{' '}
                <a href="https://stripe.com" target="_blank" rel="noreferrer" className="font-bold underline text-primary">
                    {'Stripe'}
                </a> which is an online payment
                processor. All we need is your name and email address to get started. If you do
                not have an email address, it is recommended that you simply use more
                traditional checking to make contributions.
            </p>

            {error && (
                <div className="relative px-4 py-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded" role="alert">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            <div className="flex flex-wrap mb-2 -mx-4">
                <div className="w-full px-4 sm:w-1/2">
                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="name">
                            Your Name
                        </label>
                        <input
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full px-3 py-3 leading-tight text-gray-700 border rounded shadow appearance-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            id="name"
                            name="name"
                            type="text"
                            required
                            placeholder="e.g.: John Doe"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="email">
                            Email Address
                        </label>
                        <input
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                invalidEmail ? 'border-red-500 bg-red-100' : ''
                            }`}
                            id="email"
                            name="email"
                            type="email"
                            required
                            placeholder="e.g.: johndoe@example.com"
                            inputMode="email"
                        />
                    </div>

                    {formData.email && !invalidEmail && (
                        <div className="mb-4">
                            <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="emailConfirmation">
                                Re-Enter Email
                            </label>
                            <input
                                value={formData.emailConfirmation}
                                onChange={handleInputChange}
                                className={`shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                    invalidEmailConfirmation ? 'border-red-500 bg-red-100' : ''
                                }`}
                                id="emailConfirmation"
                                name="emailConfirmation"
                                type="email"
                                required
                                inputMode="email"
                            />
                        </div>
                    )}
                </div>

                <div className="w-full px-4 sm:w-1/2">
                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="pin">
                            Security PIN
                        </label>
                        <p className="mb-2 text-sm text-gray-500">
                            Choose a secure 4 to 8 digit long number that only you know to protect your account as a PIN.
                        </p>
                        <input
                            value={formData.pin}
                            onChange={handleInputChange}
                            className={`shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                invalidPin ? 'border-red-500 bg-red-100' : ''
                            }`}
                            id="pin"
                            name="pin"
                            type="password"
                            required
                            maxLength={8}
                            inputMode="numeric"
                        />
                    </div>

                    {formData.pin && formData.pin.length >= 4 && (
                        <div className="mb-4">
                            <label className="block mb-2 text-sm font-bold text-gray-700 required" htmlFor="pinConfirmation">
                                Re-Enter PIN
                            </label>
                            <input
                                value={formData.pinConfirmation}
                                onChange={handleInputChange}
                                className={`shadow appearance-none border rounded w-full py-3 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                                    invalidPinConfirmation ? 'border-red-500 bg-red-100' : ''
                                }`}
                                id="pinConfirmation"
                                name="pinConfirmation"
                                type="password"
                                required
                                maxLength={8}
                                inputMode="numeric"
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex content-start justify-between mb-6">
                <label className="inline-flex items-start">
                    <input
                        checked={formData.terms}
                        onChange={handleInputChange}
                        name="terms"
                        className="relative mr-3 leading-tight form-checkbox text-primary"
                        type="checkbox"
                        required
                        style={{ top: '4px' }}
                    />
                    <span className="text-sm">
                        I am a member of New Heights Baptist Church in Wylie, TX and consent to the
                        processing of my contributions online according to the giving and record keeping
                        policies of the church. <span className="text-red-500">*</span>
                    </span>
                </label>
            </div>

            <div className="flex flex-wrap justify-between">
                <button
                    onClick={handleRegister}
                    disabled={disabled}
                    className="px-6 py-3 mb-4 font-bold text-white rounded bg-primary disabled:bg-blue-400 disabled:cursor-not-allowed"
                    type="button"
                >
                    {loading ? (
                        <span>
                            <FontAwesomeIcon icon={faCircleNotch} className="fas animate-spin fa-lg" />
                        </span>
                    ) : (
                        <span>
                            <FontAwesomeIcon icon={faCheckCircle} className="fas fa-lg" />
                        </span>
                    )}
                    <span className="inline-block ml-3">
                        {loading ? 'Registering...' : 'Register'}
                    </span>
                </button>

                <button
                    onClick={handleBack}
                    className="text-gray-500 truncate hover:text-primary"
                >
                    <FontAwesomeIcon icon={faReply} className="mr-2 fas" />
                    Return
                </button>
            </div>
        </div>
    )
}
